import React, { useEffect, useState, useRef } from 'react'
import { Platform } from 'react-native'
import { ArrowLeft, Printer, Check } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  ScrollView,
  Spinner,
  Text,
  TopBar,
  XStack,
  YStack,
} from '@fliisi/pollen'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'PrintTags'>
  route: RouteProp<RootStackParamList, 'PrintTags'>
}

interface Product {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  price: number
  status: string
  seller_id: string
  seller?: {
    display_name: string | null
    first_name: string | null
    last_name: string | null
  }
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  space: { name: string; space_type: string }
  market: { name: string }
}

type TagSize = 'small' | 'medium' | 'large'

const TAG_SIZES = {
  small: { width: 50, height: 30, fontSize: 8, priceSize: 12 },
  medium: { width: 70, height: 40, fontSize: 10, priceSize: 16 },
  large: { width: 90, height: 50, fontSize: 12, priceSize: 20 },
}

export default function PrintTagsScreen({ navigation, route }: Props) {
  const { reservationId, storeId } = route.params
  const { t, language } = useLanguage()
  const [products, setProducts] = useState<Product[]>([])
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const [storeName, setStoreName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tagSize, setTagSize] = useState<TagSize>('medium')
  const [showQR, setShowQR] = useState(true)
  const [showSeller, setShowSeller] = useState(true)
  const printRef = useRef<any>(null)

  useEffect(() => {
    fetchData()
  }, [reservationId, storeId])

  const fetchData = async () => {
    setLoading(true)
    try {
      if (storeId) {
        // Store (thrift shop) mode — fetch all store products
        const { data: storeData } = await supabase
          .from('flea_markets')
          .select('name')
          .eq('id', storeId)
          .single()
        setStoreName(storeData?.name || null)

        const { data: prodData, error: prodError } = await supabase
          .from('products')
          .select('id, title, title_en, title_fi, price, status, seller_id')
          .eq('flea_market_id', storeId)
          .neq('status', 'sold')
          .order('title')
        if (prodError) throw prodError
        setProducts(prodData || [])
      } else if (reservationId) {
        // Flea market reservation mode
        const { data: resData, error: resError } = await supabase
          .from('space_reservations')
          .select(`
            id, start_date, end_date,
            space:market_spaces (name, space_type),
            market:flea_markets (name)
          `)
          .eq('id', reservationId)
          .single()
        if (resError) throw resError

        setReservation({
          id: resData.id,
          start_date: resData.start_date,
          end_date: resData.end_date,
          space: Array.isArray(resData.space) ? resData.space[0] : resData.space,
          market: Array.isArray(resData.market) ? resData.market[0] : resData.market,
        })

        const { data: prodData, error: prodError } = await supabase
          .from('products')
          .select(`
            id, title, title_en, title_fi, price, status, seller_id,
            seller:users (display_name, first_name, last_name)
          `)
          .eq('reservation_id', reservationId)
          .order('title')
        if (prodError) throw prodError
        setProducts(
          (prodData || []).map((p: any) => ({
            ...p,
            seller: Array.isArray(p.seller) ? p.seller[0] : p.seller,
          }))
        )
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getTitle = (product: Product) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const getSellerName = (product: Product) => {
    if (!product.seller) return ''
    const { display_name, first_name, last_name } = product.seller
    if (display_name) return display_name
    if (first_name || last_name) return `${first_name || ''} ${last_name || ''}`.trim()
    return ''
  }

  const handlePrint = () => {
    if (Platform.OS === 'web') {
      window.print()
    }
  }

  const generateQRCodeUrl = (productId: string) => {
    const baseUrl = 'https://kirpputori.dev.sakualy.ai'
    const productUrl = `${baseUrl}/product/${productId}`
    return `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(productUrl)}`
  }

  const sizeConfig = TAG_SIZES[tagSize]

  if (loading) {
    return (
      <YStack f={1} backgroundColor="$backgroundSecondary">
        <XStack justifyContent="space-between" alignItems="center" paddingHorizontal="$md" paddingTop="$md" paddingBottom="$md" backgroundColor="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderColor">
          <XStack alignItems="center" gap="$xs" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer" paddingVertical="$sm" paddingRight="$md">
            <ArrowLeft size={18} color="var(--colorPrimary)" />
            <Text fontSize={16} color="$colorPrimary" fontWeight="500">{t('back') || 'Back'}</Text>
          </XStack>
          <Text fontSize={18} fontWeight="bold" color="$color">{t('print_tags') || 'Print Tags'}</Text>
          <YStack width={60} />
        </XStack>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      </YStack>
    )
  }

  return (
    <YStack f={1} backgroundColor="$backgroundSecondary">
      {/* Header */}
      <XStack testID="no-print" justifyContent="space-between" alignItems="center" paddingHorizontal="$md" paddingTop="$md" paddingBottom="$md" backgroundColor="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderColor">
        <XStack alignItems="center" gap="$xs" onPress={() => navigation.goBack()} pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer" paddingVertical="$sm" paddingRight="$md">
          <ArrowLeft size={18} color="var(--colorPrimary)" />
          <Text fontSize={16} color="$colorPrimary" fontWeight="500">{t('back') || 'Back'}</Text>
        </XStack>
        <Text fontSize={18} fontWeight="bold" color="$color">{t('print_tags') || 'Print Tags'}</Text>
        <YStack width={60} />
      </XStack>

      {/* Settings Bar */}
      <XStack testID="no-print" flexWrap="wrap" alignItems="center" justifyContent="space-between" padding="$md" backgroundColor="$backgroundCard" borderBottomWidth={1} borderBottomColor="$borderColor" gap="$sm">
        <XStack alignItems="center" gap="$sm">
          <Text fontSize={14} color="$colorSecondary" fontWeight="500">{t('tag_size') || 'Tag Size'}:</Text>
          <XStack gap="$xs">
            {(['small', 'medium', 'large'] as TagSize[]).map((size) => (
              <XStack
                key={size}
                paddingHorizontal="$sm" paddingVertical={6} borderRadius="$sm"
                backgroundColor={tagSize === size ? '$colorPrimary' : '$backgroundSecondary'}
                borderWidth={1} borderColor={tagSize === size ? '$colorPrimary' : '$borderColor'}
                onPress={() => setTagSize(size)}
                pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
              >
                <Text fontSize={12} fontWeight="500" textTransform="capitalize" color={tagSize === size ? '#fff' : '$colorSecondary'}>
                  {t(size) || size}
                </Text>
              </XStack>
            ))}
          </XStack>
        </XStack>

        <XStack alignItems="center" gap="$sm">
          <XStack
            paddingHorizontal="$sm" paddingVertical={6} borderRadius="$sm"
            backgroundColor={showQR ? '$colorPrimaryMuted' : '$backgroundSecondary'}
            borderWidth={1} borderColor={showQR ? '$colorPrimary' : '$borderColor'}
            onPress={() => setShowQR(!showQR)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <XStack alignItems="center" gap="$xs">
              <Text fontSize={12} fontWeight="500" color={showQR ? '$colorPrimary' : '$colorSecondary'}>QR</Text>
              {showQR && <Check size={14} color="var(--colorPrimary)" />}
            </XStack>
          </XStack>
          <XStack
            paddingHorizontal="$sm" paddingVertical={6} borderRadius="$sm"
            backgroundColor={showSeller ? '$colorPrimaryMuted' : '$backgroundSecondary'}
            borderWidth={1} borderColor={showSeller ? '$colorPrimary' : '$borderColor'}
            onPress={() => setShowSeller(!showSeller)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
          >
            <XStack alignItems="center" gap="$xs">
              <Text fontSize={12} fontWeight="500" color={showSeller ? '$colorPrimary' : '$colorSecondary'}>{t('seller') || 'Seller'}</Text>
              {showSeller && <Check size={14} color="var(--colorPrimary)" />}
            </XStack>
          </XStack>
        </XStack>

        <XStack
          backgroundColor="$colorPrimary" paddingHorizontal="$lg" paddingVertical={10} borderRadius="$md"
          onPress={handlePrint}
          pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick" cursor="pointer"
        >
          <XStack alignItems="center" gap={6}>
            <Printer size={16} color="#fff" />
            <Text color="#fff" fontSize={14} fontWeight="600">{t('print') || 'Print'}</Text>
          </XStack>
        </XStack>
      </XStack>

      {/* Info Banner */}
      {(reservation || storeName) && (
        <XStack testID="no-print" backgroundColor="$colorPrimaryMuted" padding="$md" justifyContent="space-between" alignItems="center">
          <Text fontSize={14} color="$color" fontWeight="500">
            {storeName ?? `${reservation?.market?.name} · ${reservation?.space?.name}`}
          </Text>
          <Text fontSize={14} color="$colorSecondary">
            {products.length} {t('products') || 'products'}
          </Text>
        </XStack>
      )}

      {/* Printable Tags */}
      <ScrollView f={1} contentContainerStyle={{ padding: 16 }}>
        {products.length === 0 ? (
          <YStack testID="no-print" padding="$3xl" alignItems="center">
            <Text fontSize={16} color="$colorSecondary" textAlign="center">
              {t('no_products_to_print') || 'No products linked to this reservation'}
            </Text>
          </YStack>
        ) : (
          <XStack flexWrap="wrap" gap="$sm" justifyContent="center" ref={printRef}>
            {products.map((product) => (
              <YStack
                key={product.id}
                width={sizeConfig.width * 3.78}
                height={sizeConfig.height * 3.78}
                backgroundColor="$backgroundCard"
                borderRadius="$xs"
                borderWidth={1}
                borderColor="$borderColor"
                borderStyle="dashed"
                overflow="hidden"
                position="relative"
              >
                <XStack f={1} padding="$sm">
                  {showQR && (
                    <YStack jc="center" ai="center" marginRight="$sm">
                      <img
                        src={generateQRCodeUrl(product.id)}
                        alt="QR"
                        style={{ width: sizeConfig.height * 2.5, height: sizeConfig.height * 2.5 }}
                      />
                    </YStack>
                  )}
                  <YStack f={1} jc="center" {...(!showQR && { ai: 'center' })}>
                    <Text fontWeight="600" color="$color" fontSize={sizeConfig.fontSize} marginBottom="$xs" numberOfLines={2}>
                      {getTitle(product)}
                    </Text>
                    <Text fontWeight="bold" color="$color" fontSize={sizeConfig.priceSize} marginBottom={2}>
                      €{product.price?.toFixed(2) || '0.00'}
                    </Text>
                    {showSeller && getSellerName(product) && (
                      <Text color="$colorSecondary" fontSize={sizeConfig.fontSize - 2} marginBottom={2}>
                        {getSellerName(product)}
                      </Text>
                    )}
                    <Text color="$colorSecondary" fontSize={sizeConfig.fontSize - 2} fontFamily={Platform.OS === 'web' ? 'monospace' : undefined}>
                      #{product.id.slice(-6).toUpperCase()}
                    </Text>
                  </YStack>
                </XStack>
                {/* Cut corner */}
                <YStack
                  position="absolute" top={0} right={0}
                  width={0} height={0}
                  borderWidth={8} borderColor="transparent"
                  borderTopColor="$borderColor" borderRightColor="$borderColor"
                />
              </YStack>
            ))}
          </XStack>
        )}
      </ScrollView>

      {/* Print Styles for Web */}
      {Platform.OS === 'web' && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
              @media print {
                [data-testid="no-print"] { display: none !important; }
                body { margin: 0; padding: 0; }
                @page { margin: 10mm; }
              }
            `,
          }}
        />
      )}
    </YStack>
  )
}
